<template>
  <div class="w-full bg-white rounded shadow-md border overflow">
    <div class="px-6 py-4 font-bold border-b bg-zt-white">
      <h2 class="text-zt-xl table-title">
        {{ $t('message.workflow.table.title') }}
      </h2>
    </div>
    <div v-if="loading">
      <div class="w-full h-90 flex justify-center items-center py-4">
        <div class="spinner" role="status" />
      </div>
    </div>
    <div v-else class="p-3">
      <div
        v-for="(row, index) in rows"
        :key="index"
        class="flex flex-col p-2 border rounded"
        :class="{ 'mb-4': index !== rows.length - 1 }"
      >
        <div class="flex justify-between items-center mb-2 p-3">
          <div class="w-1/2">{{ row.created_at }}</div>
          <div class="w-1/2 flex justify-end items-center">
            <zt-button
              outlined
              class="button-table-action"
              data-vs-button="btn-donwload-history-item"
              @click="$emit('workflow-selected', row)"
            >
              <div class="flex items-center">
                <zt-icon name="eye" class="mr-2" />
                <div>
                  {{ $t('message.workflow.table.seeDocument') }}
                </div>
              </div>
            </zt-button>
          </div>
        </div>

        <div class="flex items-center mb-2">
          <zt-icon name="file-text-outline" :style="{ fontSize: '2rem' }" />
          <span class="ml-2">
            {{ row.document_type.name }}
          </span>
        </div>
        <div class="flex items-center mb-2">
          <zt-chip square outline class="workflow-chip uppercase">
            {{ $t('message.workflow.table.columns.chip') }}
          </zt-chip>
          <ul class="workflow-state ml-2">
            <li
              v-for="(approve, index) in row.approves"
              :key="approve.id"
              class="mr-1"
            >
              <div
                class="flex items-center justify-center select-none custom-tooltip"
                :class="getCircleStatus(approve, row.status)"
                :data-tooltip="getTooltipContent(approve)"
              >
                {{ index + 1 }}
              </div>
            </li>
          </ul>
        </div>

         <div class="flex items-center mb-2">
          <zt-chip square outline :class="getChipClass(row)">
            {{ getStatus(row.status) }}
          </zt-chip>
        </div>

        <div class="flex justify-start items-center">
          <zt-avatar :text="row.fullname" color="secondary-100" />
          <span class="ml-2 flex flex-wrap">
            {{ row.fullname }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'WorkflowRequestMobile',
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    if (!this.loading && this.rows.length > 0) {
      this.setupTooltipListeners()
    }
  },
  beforeDestroy() {
    this.cleanupTooltipListeners()
  },
  watch: {
    loading(newValue) {
      if (!newValue && this.rows.length > 0) {
        this.$nextTick(() => {
          this.setupTooltipListeners()
        })
      }
    },
    rows() {
      if (!this.loading && this.rows.length > 0) {
        this.$nextTick(() => {
          this.setupTooltipListeners()
        })
      }
    }
  },
  methods: {
    getDate(date) {
      if (!date) return ''
      const formattedDate = moment(date, 'DD/MM/YYYY HH:mm:ss.SSSSSS')
      return formattedDate.format('DD/MM/YYYY')
    },
    getStatus(status) {
      const obj = {
        1: this.$t('message.workflow.status.coursed'),
        2: this.$t('message.workflow.status.approved'),
        3: this.$t('message.workflow.status.rejected'),
        4: this.$t('message.workflow.status.new')
      }
      return obj[status]
    },
    getCircleStatus(approve, workflowStatus) {
      const statusMap = {
        1: 'pending',
        2: 'approve',
        3: 'cancelled'
      }
      if (approve.status.id === 0 && workflowStatus === 1) {
        return 'pending'
      }
      return statusMap[approve.status.id] || {}
    },
    getChipClass(item) {
      const classMap = {
        1: 'workflow-chip-coursed',
        2: 'workflow-chip-success',
        3: 'workflow-chip-rejected'
      }
      return classMap[item.status] || {}
    },
    getTooltipContent(approve) {
      let content = approve.group.name.toUpperCase()
      if (approve.status.id === 2) {
        content += `\n${this.$t('message.workflow.table.approveBy')} ${approve.fullname}`
      }
      if (approve.status.id === 3) {
        content += `\n${this.$t('message.workflow.table.rejectedBy')} ${approve.fullname}`
      }
      if (approve.status.id === 3 || (approve.status.id === 2 && approve.reviewed_at)) {
        content += `\n${this.$t('message.workflow.table.atDay')} ${this.getDate(approve.reviewed_at)}`
      }
      if (approve.comment) {
        content += `\n\n${this.$t('message.workflow.table.comment')} ${approve.comment}`
      }
      return content
    },
    setupTooltipListeners() {
      this.cleanupTooltipListeners()
      this.$nextTick(() => {
        const tooltips = this.$el.querySelectorAll('.custom-tooltip')  
        tooltips.forEach(tooltip => {
          tooltip.addEventListener('click', this.handleTooltipClick)
        })
        
        document.addEventListener('click', this.handleClickOutside)
      })
    },
    cleanupTooltipListeners() {
      if (this.$el) {
        const tooltips = this.$el.querySelectorAll('.custom-tooltip')
        tooltips.forEach(tooltip => {
          tooltip.removeEventListener('click', this.handleTooltipClick)
          tooltip.classList.remove('active')
        })
      }
      document.removeEventListener('click', this.handleClickOutside)
    },
    handleTooltipClick(e) {
      e.preventDefault()
      e.stopPropagation()
      const tooltips = this.$el.querySelectorAll('.custom-tooltip')
      tooltips.forEach(t => t.classList.remove('active'))
      e.currentTarget.classList.toggle('active')
    },
    handleClickOutside(e) {
      const tooltips = this.$el.querySelectorAll('.custom-tooltip')
      const clickedInsideTooltip = Array.from(tooltips).some(tooltip => 
        tooltip.contains(e.target)
      )
      
      if (!clickedInsideTooltip) {
        tooltips.forEach(t => t.classList.remove('active'))
      }
    }
  }
}
</script>

<style scoped lang="scss">
.table-title {
  color: #2d2d2d;
}

.workflow-state {
  li {
    display: inline-block;
    vertical-align: center;
    div {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #707070;
      font-size: 12px;
      color: #fff;
    }
    div.approve {
      background: #7faf7f;
    }
    div.cancelled {
      background: #f16b6c;
    }
    div.pending {
      background: #fb8c00;
    }
  }
}

::v-deep {
  .button-table-action.zt-button {
    background: #fff !important;
    color: #707070 !important;
    border-color: #707070 !important;
    .zt-icon {
      font-size: 1rem;
    }
  }
  .zt-chip {
    min-width: 140px;
    justify-content: center;
    margin: 0;
  }
  .workflow-chip {
    background: #eae8f4 !important;
    color: #4f41a3;
  }
  .workflow-chip-coursed {
    background: #e1f5fe !important;
    color: #017eec;
  }
  .workflow-chip-rejected {
    background: #ffd4d8 !important;
    color: #f16b6c;
  }
  .workflow-chip-success {
    background: #e5fff7 !important;
    color: #54caa6;
  }
}

.spinner {
  border: 3px solid rgba(0, 0, 0, 0.1);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border-left-color: #4f41a3;
  animation: spin .5s ease infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Tooltip personalizado para mobile */
.custom-tooltip {
  position: relative;
  cursor: pointer;
}

.custom-tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  white-space: pre-line;
  width: max-content;
  max-width: 200px;
  min-width: 80px;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  border: 1px solid #555;
  text-align: left;
  
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  line-height: 1.3;
  padding: 6px 10px;
}

.custom-tooltip::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 1px);
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #333;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1001;
}

.custom-tooltip.active::after,
.custom-tooltip.active::before {
  opacity: 1;
  visibility: visible;
}

.custom-tooltip[data-tooltip*="\n"]::after {
  max-width: 200px;
}
</style>